/**
 * EmailDebtPanel — unanswered emails (received with no reply sent).
 */

import React, { useState } from 'react';
import { Table, Button, Modal, Typography, Tag, Avatar } from 'antd';
import { MailOpen, ArrowRight } from 'lucide-react';

const { Text } = Typography;

function getInitials(email = '') {
  return email.slice(0, 2).toUpperCase();
}

function avatarColor(email) {
  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (const c of email) hash = c.charCodeAt(0) + hash * 31;
  return colors[Math.abs(hash) % colors.length];
}

function fmtDate(date) {
  if (!date) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const columns = [
  {
    title: 'Sender',
    dataIndex: 'from',
    key: 'from',
    render: (from, row) => {
      const nameMatch = from.match(/^"?([^"<]+)"?\s*</);
      const name = nameMatch ? nameMatch[1].trim() : row.email;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar size={28} style={{ background: avatarColor(row.email), fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
            {getInitials(row.email)}
          </Avatar>
          <Text strong ellipsis style={{ maxWidth: 160, fontSize: 13 }}>{name}</Text>
        </div>
      );
    },
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    ellipsis: true,
    render: (email) => <Text type="secondary" style={{ fontSize: 13 }}>{email}</Text>,
  },
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    width: 130,
    render: (date) => <Text type="secondary" style={{ fontSize: 13 }}>{fmtDate(date)}</Text>,
  },
  {
    title: 'Status',
    key: 'status',
    width: 110,
    render: () => <Tag color="red" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>Unanswered</Tag>,
  },
];

export function EmailDebtPanel({ metrics, isDark, externalOpen, onExternalClose }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const modalOpen = externalOpen || internalOpen;
  const closeModal = () => { setInternalOpen(false); if (onExternalClose) onExternalClose(); };

  const { emailDebt } = metrics;

  const preview = emailDebt.slice(0, 5);
  const hasMore = emailDebt.length > 5;

  const previewData = preview.map((d, i) => ({ ...d, key: i }));
  const allData = emailDebt.map((d, i) => ({ ...d, key: i }));

  return (
    <>
      <div style={{
        borderRadius: 14,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
        boxShadow: isDark
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        padding: '20px',
        marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: isDark ? 'rgba(249,115,22,0.15)' : 'rgba(249,115,22,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MailOpen size={16} color="#f97316" strokeWidth={2} />
            </div>
            <Text strong style={{ fontSize: 14 }}>Email Debt</Text>
          </div>
          <Tag color="orange" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, margin: 0 }}>
            {emailDebt.length} unanswered
          </Tag>
        </div>

        {emailDebt.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>No unanswered emails — inbox zero!</Text>
          </div>
        ) : (
          <>
            <Table
              dataSource={previewData}
              columns={columns}
              pagination={false}
              size="small"
              showHeader
            />
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <Button
                  type="text"
                  onClick={() => setInternalOpen(true)}
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#f97316',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  View all {emailDebt.length} unanswered emails
                  <ArrowRight size={13} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MailOpen size={16} color="#f97316" />
            <span>All Unanswered Emails</span>
            <Tag color="orange" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, marginLeft: 4 }}>{emailDebt.length}</Tag>
          </div>
        }
        width={700}
        styles={{ body: { padding: '16px 0 0' } }}
      >
        <Table
          dataSource={allData}
          columns={columns}
          pagination={{ pageSize: 15, size: 'small', showSizeChanger: false }}
          size="small"
          scroll={{ y: 420 }}
        />
      </Modal>
    </>
  );
}
